import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import {
  Sun, Cloud, CloudRain, Wifi, Car, MapPin, CalendarDays, BookOpen,
  ChevronRight, Bell, Coffee, Lightbulb, Library, ClipboardList, FileText,
  Globe, Instagram, Award, Users, Sparkles, Leaf, ShoppingCart,
  Home as HomeIcon, Map as MapIcon, CheckCircle, KeyRound,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import ShoppingRun from "../ShoppingRun";
import { InstallAppCard } from "../InstallAppCard";
import { PushPromptCard } from "../PushPromptCard";
import AvatarNudgeBanner from "../AvatarNudgeBanner";
import RetreatBanner from "./RetreatBanner";
import PointsStrip from "./PointsStrip";
import { useT } from "../../lib/i18n";
import { SAVI_SOCIAL } from "../../config/saviImages";

const WeatherGlyph = ({ description, className = "w-4 h-4" }) => {
  const d = (description || "").toLowerCase();
  if (/(rain|drizzle|shower|thunder|storm|sleet|snow)/.test(d)) return <CloudRain className={className} strokeWidth={1.6} />;
  if (/(cloud|overcast|mist|fog|haze)/.test(d)) return <Cloud className={className} strokeWidth={1.6} />;
  return <Sun className={className} strokeWidth={1.6} />;
};

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#8C9A7C] px-1 mb-2.5">{children}</p>
);

const IconChip = ({ icon: Icon, tone = "terracotta", className = "" }) => {
  const tones = {
    terracotta: "bg-[#C85A38]/12 text-[#C85A38]",
    sage: "bg-[#8C9A7C]/15 text-[#5C6A4C]",
    gold: "bg-[#C89B7B]/20 text-[#A9744B]",
    ink: "bg-[#1A1A1A]/8 text-[#1A1A1A]",
  };
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${tones[tone]} ${className}`}>
      <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
    </div>
  );
};

const CardTitle = ({ icon, tone, children }) => (
  <div className="flex items-center gap-2.5">
    <IconChip icon={icon} tone={tone} className="w-8 h-8" />
    <h3 className="font-serif text-lg text-[#1A1A1A]">{children}</h3>
  </div>
);

const mondayISO = () => {
  const today = new Date();
  const dow = today.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(today); mon.setDate(today.getDate() + diff); mon.setHours(0, 0, 0, 0);
  return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, "0")}-${String(mon.getDate()).padStart(2, "0")}`;
};

const FIRST_WEEK_TIPS = [
  { icon: HomeIcon, title: "Settle In", desc: "Take your time to explore the villa. Find your favorite spots to work and relax." },
  { icon: Coffee, title: "Morning Kitchen Vibes", desc: "People start milling around the kitchen from 9am. Grab a coffee and meet your housemates." },
  { icon: Users, title: "Introduce Yourself", desc: "Don't be shy! Everyone was new once. People here love meeting new humans." },
  { icon: MapIcon, title: "Explore Náquera", desc: "Take a walk around the village. Visit the local bakery and enjoy the mountain views." },
  { icon: Wifi, title: "Find Your Rhythm", desc: "Work when you work best. The quiet hours (10pm-8am) help everyone focus and rest." },
];

export default function HomeTab({
  user, weather, todayActivities, todayEvents = [], weekEvents, todayVolunteerTasks,
  steveMessage, announcements, youAsked, countdown, todayRides: todayRidesProp = [], weekRides: weekRidesProp = [],
  progressPercent, showFirstWeekTips, onOpenNotifications, headers, heroUrl,
}) {
  const navigate = useNavigate();
  const [firstWeekOpen, setFirstWeekOpen] = useState(false);
  const [rsvpState, setRsvpState] = useState({});
  const [rsvpBusy, setRsvpBusy] = useState(null);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [talkOpen, setTalkOpen] = useState(false);
  const [talk, setTalk] = useState({ topic: "", date: todayISO, time: "10:00", location: "Living Room" });
  const [submittingTalk, setSubmittingTalk] = useState(false);
  const [topScores, setTopScores] = useState([]);

  useEffect(() => {
    axios.get(`${API}/points/leaderboard?period=week&limit=3`)
      .then((r) => setTopScores((r.data?.leaderboard || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  const submitTalk = async () => {
    if (!talk.topic.trim() || !talk.date) { toast.error("Give your talk a topic and a date"); return; }
    setSubmittingTalk(true);
    try {
      await axios.post(`${API}/community/espresso-talks`, { ...talk, topic: talk.topic.trim(), duration_minutes: 20 }, { headers });
      toast.success("Espresso Talk scheduled ☕ — it's on the calendar");
      setTalkOpen(false);
      setTalk({ topic: "", date: todayISO, time: "10:00", location: "Living Room" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't schedule the talk");
    } finally {
      setSubmittingTalk(false);
    }
  };

  const isAdmin = user?.role === "admin";
  const isVolunteer = !!user?.is_volunteer;
  const isRetreat = user?.guest_type === "retreat";
  // Retreat guests are restricted to the programme — no ride shares anywhere on Home.
  const todayRides = isRetreat ? [] : todayRidesProp;
  const weekRides = isRetreat ? [] : weekRidesProp;
  const { t, locale } = useT();
  const firstName = (user?.profile?.preferred_name || user?.name || "").split(" ")[0];
  const hour = new Date().getHours();
  const greeting = t(hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening");
  const dateLine = new Date().toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });

  const copyWifi = async () => {
    try { await navigator.clipboard.writeText("valencia"); } catch (e) { /* clipboard blocked, still show */ }
    toast.success(`${t("WiFi password copied")} — \u201Cvalencia\u201D`);
  };

  const quickTile = ({ key, icon, tone, label, sub, subAccent = false, to, onClick, testid }) => {
    const inner = (
      <div className="flex items-center gap-3">
        <IconChip icon={icon} tone={tone} />
        <div className="min-w-0">
          <p className="font-medium text-[#1A1A1A] text-sm leading-tight">{label}</p>
          <p className={`text-[11px] mt-0.5 truncate ${subAccent ? "text-[#C85A38] font-medium" : "text-[#1A1A1A]/50"}`}>{sub}</p>
        </div>
      </div>
    );
    const cls = "bg-white rounded-xl px-3.5 py-3 border border-stone-200 shadow-sm shadow-stone-200/50 text-left active:scale-[0.97] transition-transform block w-full";
    if (onClick) return <button key={key} onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
    return <Link key={key} to={to} className={cls} data-testid={testid}>{inner}</Link>;
  };

  const toggleRsvp = async (ev, going) => {
    if (!headers || rsvpBusy) return;
    setRsvpBusy(ev.id);
    try {
      const res = await axios.post(`${API}/community/events/rsvp`, { occurrence_key: ev.id, going }, { headers });
      setRsvpState((p) => ({
        ...p,
        [ev.id]: { going, count: res.data?.attendee_count ?? Math.max(0, (ev.attendee_count || 0) + (going ? 1 : -1)) },
      }));
      toast.success(going ? "You're in! 🙌" : "Removed from your list");
    } catch (e) {
      toast.error("Couldn't update RSVP");
    } finally {
      setRsvpBusy(null);
    }
  };

  const [rideState, setRideState] = useState({}); // ride.id → participants[] (optimistic overrides)
  const [rideBusy, setRideBusy] = useState(null);

  const toggleRide = async (ride, join) => {
    if (!headers || rideBusy) return;
    setRideBusy(ride.id);
    try {
      await axios.post(`${API}/rides/${ride.id}/${join ? "join" : "leave"}`, join ? { seats_needed: 1 } : {}, { headers });
      const current = rideState[ride.id] || ride.participants || [];
      setRideState((p) => ({
        ...p,
        [ride.id]: join
          ? [...current, { user_id: user?.id, user_name: user?.name, seats: 1 }]
          : current.filter((x) => x.user_id !== user?.id),
      }));
      toast.success(join ? (ride.ride_type === "offering" ? "Seat saved 🚗" : "Thanks — they'll be chuffed 🚗") : "You're off this ride");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update ride");
    } finally {
      setRideBusy(null);
    }
  };

  const rideRow = (ride) => {
    const offering = ride.ride_type === "offering";
    const participants = rideState[ride.id] || ride.participants || [];
    const taken = participants.reduce((s, p) => s + (p.seats || 1), 0);
    const seatsLeft = Math.max(0, (ride.seats || 1) - taken);
    const who = (ride.created_by_name || "").split(" ")[0];
    const mine = ride.created_by_id === user?.id;
    const joined = participants.some((p) => p.user_id === user?.id);
    const canJoin = !mine && !joined && (!offering || seatsLeft > 0);
    const meta = offering
      ? (seatsLeft > 0 ? `${seatsLeft} ${t(seatsLeft === 1 ? "seat left" : "seats left")}` : t("Full"))
      : `${ride.seats || 1} ${t((ride.seats || 1) === 1 ? "seat needed" : "seats needed")}`;
    const busy = rideBusy === ride.id;
    return (
      <Link
        key={`ride-${ride.id}`}
        to="/community?tab=rides"
        className="flex items-center gap-3 bg-[#FBF7F1] rounded-lg px-3 py-2.5 border-l-2 border-[#C89B7B] active:scale-[0.98] transition-transform"
        data-testid={`home-ride-row-${ride.id}`}
      >
        <Car className="w-5 h-5 text-[#C89B7B] flex-shrink-0" strokeWidth={1.6} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[#1A1A1A] text-[13px] leading-tight truncate">
            {offering ? `${t("Lift to")} ${ride.destination}` : `${t("Lift wanted to")} ${ride.destination}`}
          </p>
          <p className="text-[11px] text-[#1A1A1A]/50 truncate mt-0.5">
            {ride.time}{ride.return_trip && ride.return_time ? ` · ${t("back")} ${ride.return_time}` : ""}{who ? ` · ${who}` : ""} · {meta}
          </p>
        </div>
        {mine ? (
          <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#C89B7B]">{t("Yours")}</span>
        ) : joined || canJoin ? (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleRide(ride, !joined); }}
            disabled={busy}
            className={`flex-shrink-0 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-colors active:scale-[0.94] ${
              joined ? "bg-[#8C9A7C] text-white" : "bg-white border border-[#C89B7B]/60 text-[#A67C5B]"
            } ${busy ? "opacity-50" : ""}`}
            data-testid={`home-ride-join-${ride.id}`}
          >
            {joined ? (offering ? t("✓ Seat") : t("✓ Driving")) : (offering ? t("Join") : t("I can drive"))}
          </button>
        ) : (
          <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#1A1A1A]/35">{t("Full")}</span>
        )}
      </Link>
    );
  };

  const eventRow = (ev, key) => {
    const st = rsvpState[ev.id];
    const going = st ? st.going : !!ev.i_am_going;
    const count = st ? st.count : (ev.attendee_count || 0);
    return (
      <Link
        key={key}
        to={`/community?tab=activities&event=${encodeURIComponent(ev.id)}`}
        className={`flex items-center gap-3 bg-[#FBF7F1] rounded-lg px-3 py-2.5 active:scale-[0.98] transition-transform ${ev.type === "espresso" ? "border-l-2 border-[#C85A38]" : ""}`}
        data-testid={`home-event-row-${ev.id}`}
      >
        <span className="text-lg flex-shrink-0 w-6 text-center leading-none">{ev.emoji || "•"}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[#1A1A1A] text-[13px] leading-tight line-clamp-2 break-words">{ev.name}</p>
          <p className="text-[11px] text-[#1A1A1A]/50 truncate mt-0.5">
            {ev.time || ev.time_slot}{ev.location ? ` · ${ev.location}` : ""}{!isRetreat && count > 0 ? ` · ${count} in` : ""}
          </p>
        </div>
        {!isRetreat && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleRsvp(ev, !going); }}
          disabled={rsvpBusy === ev.id}
          className={`flex-shrink-0 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-colors active:scale-[0.94] ${
            going ? "bg-[#8C9A7C] text-white" : "bg-white border border-[#C85A38]/40 text-[#C85A38]"
          } ${rsvpBusy === ev.id ? "opacity-50" : ""}`}
          data-testid={`home-rsvp-${ev.id}`}
        >
          {going ? t("✓ In") : t("In?")}
        </button>
        )}
      </Link>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="home-tab">

      {/* ---- Greeting ---- */}
      <div className="px-1 pt-1" data-testid="home-greeting">
        <h1 className="font-serif text-3xl text-[#1A1A1A] leading-tight">
          {greeting}{firstName ? `, ${firstName}` : ""}
        </h1>
        <div className="flex items-center gap-2.5 mt-2.5">
          <p className="text-sm text-[#1A1A1A]/55">{dateLine}</p>
          {weather && (
            <span className="inline-flex items-center gap-1.5 bg-white border border-stone-200 rounded-full pl-2 pr-2.5 py-0.5 text-[13px] text-[#1A1A1A]/75" data-testid="greeting-weather-pill">
              <WeatherGlyph description={weather.description} className="w-3.5 h-3.5 text-[#C85A38]" />
              {weather.temp}°
            </span>
          )}
        </div>
      </div>

      {/* ---- Hero image (placeholder keeps the layout steady until the configured photo is known) ---- */}
      {heroUrl ? (
        <div className="relative rounded-xl overflow-hidden border border-stone-200 shadow-sm shadow-stone-200/50" data-testid="dashboard-hero">
          <img
            src={heroUrl}
            alt="Savi Coliving"
            className="w-full h-40 sm:h-52 object-cover"
            data-testid="dashboard-hero-image"
          />
        </div>
      ) : (
        <div className="h-40 sm:h-52 rounded-xl bg-[#E8DAC8]/60 animate-pulse" data-testid="dashboard-hero-placeholder" />
      )}

      {/* ---- Retreat strip ---- */}
      {isRetreat && <RetreatBanner retreat={user.retreat} />}

      {/* ---- Countdown to arrival (upcoming guests) ---- */}
      {countdown !== null && countdown > 0 && !isVolunteer && !isAdmin && !isRetreat && (
        <div className="flex items-center gap-3 bg-[#C85A38]/8 border border-[#C85A38]/20 rounded-xl px-4 py-3" data-testid="countdown-banner">
          <IconChip icon={CalendarDays} tone="terracotta" />
          <p className="text-sm text-[#1A1A1A]"><span className="font-semibold">{countdown} days</span> until you arrive — we're getting your room ready.</p>
        </div>
      )}

      {/* ---- Onboarding progress (slim) ---- */}
      {!isVolunteer && !isAdmin && progressPercent < 100 && (
        <Link to="/onboarding" className="block bg-white border border-stone-200 rounded-xl px-4 py-3 active:scale-[0.98] transition-transform" data-testid="onboarding-bar">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[13px] font-medium text-[#1A1A1A]">Finish your onboarding</p>
            <span className="text-[13px] font-semibold text-[#C85A38]">{progressPercent}% →</span>
          </div>
          <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-[#C85A38]" style={{ width: `${progressPercent}%` }} />
          </div>
        </Link>
      )}

      {/* ---- Volunteer shifts — first thing volunteers see ---- */}
      {(isVolunteer || isAdmin) && (isAdmin ? todayVolunteerTasks.length > 0 : true) && (
        <div>
          <SectionLabel>{isAdmin ? "Team today" : "Your shifts"}</SectionLabel>
          <div className="bg-[#8C9A7C]/10 border border-[#8C9A7C]/25 rounded-xl p-4" data-testid="volunteer-today-tasks-card">
            <div className="flex items-center justify-between mb-3">
              <CardTitle icon={ClipboardList} tone="sage">{isAdmin ? "Today's team roster" : "Today's tasks"}</CardTitle>
              <Link to={`/volunteer-schedule/${mondayISO()}`} className="text-xs text-[#5C6A4C] font-semibold" data-testid="volunteer-full-plan-link">
                Full plan →
              </Link>
            </div>
            {todayVolunteerTasks.length === 0 ? (
              <div className="rounded-lg bg-white/60 px-3 py-3.5 text-center">
                <p className="text-sm text-[#1A1A1A]/70">You're off today — enjoy!</p>
                <p className="text-[11px] text-[#1A1A1A]/45 mt-0.5">Tap "Full plan" to see the week</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {todayVolunteerTasks.map((t, idx) => (
                  <div key={idx} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 bg-white/70 ${t.highlight ? "border-l-2 border-[#C85A38]" : ""}`}>
                    <Leaf className="w-4 h-4 text-[#5C6A4C] flex-shrink-0" strokeWidth={1.6} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A1A1A] text-[13px] truncate">{t.name}</p>
                      {isAdmin ? (
                        <p className="text-[11px] text-[#1A1A1A]/50 truncate">{t.allNames.join(", ") || "—"}</p>
                      ) : (
                        t.allNames.length > 1 && (
                          <p className="text-[11px] text-[#1A1A1A]/50 truncate">with {t.allNames.filter((n) => !t.names.includes(n)).join(", ") || "team"}</p>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <PointsStrip headers={headers} isRetreat={isRetreat} />

      {/* ---- Today at Savi ---- */}
      <div>
        <SectionLabel>{t("Today")}</SectionLabel>
        <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm shadow-stone-200/50" data-testid="happening-today-card">
          <div className="flex items-center justify-between mb-3.5">
            <CardTitle icon={CalendarDays} tone="terracotta">{t("Today at Savi")}</CardTitle>
            <Link to="/community?tab=activities" className="text-xs text-[#C85A38] font-semibold" data-testid="today-open-calendar-link">{t("Calendar →")}</Link>
          </div>
          {todayEvents.length > 0 || todayRides.length > 0 ? (
            <div className="space-y-1.5">
              {todayEvents.map((ev) => eventRow(ev, ev.id))}
              {todayRides.map(rideRow)}
            </div>
          ) : isRetreat ? (
            <div className="bg-[#FBF7F1] rounded-lg px-3 py-4 text-center" data-testid="today-empty-retreat">
              <p className="text-[13px] text-[#1A1A1A]/55">{t("Nothing on the calendar today.")}</p>
            </div>
          ) : (
            <Link to="/community?tab=activities" className="block bg-[#FBF7F1] rounded-lg px-3 py-4 text-center active:scale-[0.98] transition-transform" data-testid="today-empty-add-link">
              <p className="text-[13px] text-[#1A1A1A]/55">{t("Nothing on the calendar today.")}</p>
              <p className="text-xs text-[#C85A38] font-semibold mt-1">{t("+ Add an event")}</p>
            </Link>
          )}
        </div>
      </div>

      {/* ---- This week ---- */}
      {(weekEvents.length > 0 || weekRides.length > 0) && (
        <div>
          <SectionLabel>{t("This week")}</SectionLabel>
          <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm shadow-stone-200/50" data-testid="week-ahead-card">
            <div className="flex items-center justify-between mb-3.5">
              <CardTitle icon={Sparkles} tone="gold">{t("Coming up")}</CardTitle>
              <Link to="/community?tab=activities" className="text-xs text-[#C85A38] font-semibold" data-testid="week-open-calendar-link">{t("Calendar →")}</Link>
            </div>
            <div className="space-y-4">
              {[...new Set([...weekEvents, ...weekRides].map((e) => e.date))].sort().map((date) => {
                const dayEvents = weekEvents.filter((e) => e.date === date);
                const dayRides = weekRides.filter((r) => r.date === date);
                const d = new Date(`${date}T00:00:00`);
                const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
                const isTomorrow = d.toDateString() === tomorrow.toDateString();
                return (
                  <div key={date} data-testid={`week-ahead-day-${date}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8C9A7C] mb-1.5 px-1">
                      {isTomorrow ? t("Tomorrow") : d.toLocaleDateString(locale, { weekday: "long" })}
                      <span className="text-[#1A1A1A]/30 font-medium normal-case tracking-normal"> · {d.toLocaleDateString(locale, { day: "numeric", month: "short" })}</span>
                    </p>
                    <div className="space-y-1.5">
                      {dayEvents.map((ev) => eventRow(ev, ev.id))}
                      {dayRides.map(rideRow)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ---- Quick actions ---- */}
      <div>
        <SectionLabel>{t("Quick actions")}</SectionLabel>
        <div className="grid grid-cols-2 gap-2.5">
          {quickTile({ key: "wifi", icon: Wifi, tone: "terracotta", label: t("WiFi"), sub: t("Tap to copy password"), onClick: copyWifi, testid: "quick-wifi-tile" })}
          {isRetreat
            ? quickTile({ key: "programme", icon: CalendarDays, tone: "gold", label: t("Programme"), sub: t("The full retreat schedule"), to: "/community?tab=activities", testid: "quick-programme-tile" })
            : quickTile({ key: "rides", icon: Car, tone: "gold", label: t("Rides"), sub: todayRides.length > 0 ? `${todayRides.length} ${t("today")}` : t("Offer or request"), to: "/community?tab=rides", testid: "quick-rides-tile" })}
          {!isVolunteer && !isAdmin && !isRetreat ? (
            user?.arrival
              ? quickTile({ key: "arrival", icon: MapPin, tone: "sage", label: "Arrival", sub: `${new Date(user.arrival.arrival_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · ${user.arrival.arrival_time}`, to: "/arrival", testid: "quick-arrival-tile" })
              : quickTile({ key: "arrival", icon: MapPin, tone: "sage", label: "Arrival", sub: "Set your arrival →", subAccent: true, to: "/arrival", testid: "quick-arrival-tile" })
          ) : (
            quickTile({ key: "guide", icon: MapIcon, tone: "sage", label: t("Area guide"), sub: t("Beaches, trails & town"), to: "/guide", testid: "quick-guide-tile" })
          )}
          {quickTile({ key: "handbook", icon: BookOpen, tone: "ink", label: t("Handbook"), sub: t("House info & tips"), to: "/handbook", testid: "quick-handbook-tile" })}
        </div>

        {/* Shopping broadcast — one-tap alert to the house (coliving guests only) */}
        {!isRetreat && (
          <div className="mt-2.5">
            <ShoppingRun user={user} />
          </div>
        )}
      </div>

      {/* ---- 3-day outlook ---- */}
      {weather?.forecast?.length > 0 && (
        <div>
          <SectionLabel>{t("Weather")}</SectionLabel>
          <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm shadow-stone-200/50" data-testid="weather-forecast-strip">
            <div className="grid grid-cols-3 divide-x divide-stone-100">
              {weather.forecast.slice(0, 3).map((day, idx) => (
                <div key={day.date} className="flex flex-col items-center gap-1 px-1" data-testid={`forecast-day-${idx}`}>
                  <p className="text-[11px] font-medium text-[#1A1A1A]/60">
                    {idx === 0 ? t("Today") : new Date(day.date + "T12:00:00").toLocaleDateString(locale, { weekday: "short" })}
                  </p>
                  <WeatherGlyph description={day.description} className="w-5 h-5 text-[#C89B7B]" />
                  <p className="text-xs text-[#1A1A1A]">
                    <span className="font-semibold">{day.max_temp}°</span>
                    <span className="text-[#1A1A1A]/40"> / {day.min_temp}°</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---- From the house ---- */}
      {(steveMessage || announcements.length > 0 || youAsked.length > 0) && (
        <div>
          <SectionLabel>{t("From the house")}</SectionLabel>
          <div className="space-y-2.5">
            {steveMessage && (
              <div className="bg-[#1A1A1A] rounded-xl p-4" data-testid="steve-message-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-serif text-sm">S</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-white/45">From Steve</p>
                    <p className="text-sm text-white/85 mt-1 font-serif italic leading-relaxed">"{steveMessage}"</p>
                  </div>
                </div>
              </div>
            )}

            {announcements.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm shadow-stone-200/50" data-testid="announcements-preview-card">
                <div className="flex items-center justify-between mb-3.5">
                  <CardTitle icon={Bell} tone="terracotta">{t("Latest updates")}</CardTitle>
                  <button onClick={onOpenNotifications} className="text-xs text-[#C85A38] font-semibold" data-testid="see-all-updates-btn">All updates →</button>
                </div>
                <div className="space-y-3">
                  {announcements.slice(0, 2).map((ann) => (
                    <div key={ann.id} className="bg-[#FBF7F1] rounded-lg px-3 py-2.5">
                      <p className="font-medium text-[#1A1A1A] text-[13px] leading-tight">{ann.title}</p>
                      <p className="text-[12px] text-[#1A1A1A]/55 mt-1 line-clamp-2">{ann.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {youAsked.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm shadow-stone-200/50" data-testid="you-asked-card">
                <div className="mb-3.5">
                  <CardTitle icon={CheckCircle} tone="sage">{t("You asked, we did")}</CardTitle>
                </div>
                <div className="space-y-3">
                  {youAsked.slice(0, 3).map((item) => (
                    <div key={item.id} className="border-l-2 border-[#8C9A7C]/40 pl-3" data-testid={`you-asked-row-${item.id}`}>
                      <p className="text-[#1A1A1A]/50 text-xs italic">"{item.asked}"</p>
                      <p className="text-[#1A1A1A] text-[13px] mt-0.5">{item.did}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Self-hiding prompts ---- */}
      <InstallAppCard />
      <PushPromptCard />
      <AvatarNudgeBanner user={user} onAddPhoto={() => navigate("/dashboard?tab=profile")} />

      {/* ---- More at Savi (coliving guests only — retreat guests stay on the programme) ---- */}
      {!isRetreat && <div>
        <SectionLabel>{t("More at Savi")}</SectionLabel>

        {/* House Points — the game, with this season's top 3 (coliving guests only) */}
        <Link
          to="/community?tab=game"
          className="block bg-white rounded-xl px-3.5 py-3 border border-stone-200 shadow-sm shadow-stone-200/50 active:scale-[0.98] transition-transform mb-2.5"
          data-testid="more-tile-house-points"
        >
          <div className="flex items-center gap-3">
            <IconChip icon={Award} tone="gold" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-[#1A1A1A] text-sm leading-tight">House Points</p>
              <p className="text-[11px] text-[#1A1A1A]/50 mt-0.5">This week's podium — resets Monday. Sunday night crowns the MVP</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#1A1A1A]/20 flex-shrink-0" strokeWidth={1.6} />
          </div>
          {topScores.length > 0 && (
            <div className="mt-2.5 grid grid-cols-3 gap-2" data-testid="house-points-top3">
              {topScores.map((e, i) => (
                <div key={e.email || i} className="flex items-center gap-1.5 bg-[#FBF7F1] rounded-lg px-2 py-1.5 min-w-0">
                  <span className="text-sm flex-shrink-0">{["🥇", "🥈", "🥉"][i]}</span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-[#1A1A1A] truncate">{(e.name || "?").split(" ")[0]}</p>
                    <p className="text-[10px] text-[#C85A38] font-semibold">{e.points} pts</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Link>

        <div className="grid grid-cols-2 gap-2.5">
          {[
            { id: "library", icon: Library, tone: "sage", label: t("Savi Library"), sub: t("Reads & recipes"), to: "/library" },
            { id: "host-talk", icon: Coffee, tone: "terracotta", label: t("Host a talk"), sub: t("20 min on anything"), onClick: () => setTalkOpen(true) },
            { id: "feedback", icon: Lightbulb, tone: "terracotta", label: t("Got an idea?"), sub: t("Tell Steve in 30s"), to: "/feedback?from=app" },
            ...(isVolunteer || isAdmin ? [{ id: "team-plan", icon: ClipboardList, tone: "sage", label: "Team plan", sub: "This week's chores", to: `/volunteer-schedule/${mondayISO()}` }] : []),
            ...(isVolunteer ? [{ id: "guidelines", icon: FileText, tone: "sage", label: "Guidelines", sub: "How shifts work", href: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/wija5c7b_Savi%20Volunteer%20Guidelines%20%281%29.pdf" }] : []),
            ...(showFirstWeekTips && !isVolunteer ? [{ id: "first-week", icon: Sparkles, tone: "gold", label: "First week tips", sub: "5 ways to settle in", onClick: () => setFirstWeekOpen(true) }] : []),
            { id: "sober-nomads", icon: Globe, tone: "sage", label: t("Sober Nomads"), sub: t("Our free community"), href: "https://www.facebook.com/groups/1537243484868294" },
            { id: "instagram", icon: Instagram, tone: "terracotta", label: t("Tag us"), sub: "@savicoliving", href: SAVI_SOCIAL.instagram },
          ].map((tile) => {
            const inner = (
              <div className="flex items-center gap-3">
                <IconChip icon={tile.icon} tone={tile.tone} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#1A1A1A] text-sm leading-tight">{tile.label}</p>
                  <p className="text-[11px] text-[#1A1A1A]/50 mt-0.5 truncate">{tile.sub}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#1A1A1A]/20 flex-shrink-0" strokeWidth={1.6} />
              </div>
            );
            const cls = "bg-white rounded-xl px-3.5 py-3 border border-stone-200 shadow-sm shadow-stone-200/50 text-left active:scale-[0.97] transition-transform block w-full";
            if (tile.onClick) return <button key={tile.id} onClick={tile.onClick} className={cls} data-testid={`more-tile-${tile.id}`}>{inner}</button>;
            if (tile.href) return <a key={tile.id} href={tile.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`more-tile-${tile.id}`}>{inner}</a>;
            return <Link key={tile.id} to={tile.to} className={cls} data-testid={`more-tile-${tile.id}`}>{inner}</Link>;
          })}
        </div>
      </div>}

      {/* Host an Espresso Talk sheet — focused, no distractions */}
      <Sheet open={talkOpen} onOpenChange={setTalkOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl bg-[#FBF7F1] max-h-[88vh] overflow-y-auto" data-testid="host-talk-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="font-serif text-2xl text-[#1A1A1A]">Host an Espresso Talk</SheetTitle>
            <SheetDescription className="text-sm text-[#1A1A1A]/55">
              20 minutes on anything you know or love — the house shows up, coffee in hand.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-3.5 pb-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#8C9A7C] block mb-1.5">Topic</label>
              <Input
                value={talk.topic}
                onChange={(e) => setTalk({ ...talk, topic: e.target.value })}
                placeholder="e.g. How I quit my job and moved to Spain"
                className="bg-white border-stone-200 text-sm"
                data-testid="talk-topic-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#8C9A7C] block mb-1.5">Date</label>
                <Input
                  type="date"
                  min={todayISO}
                  value={talk.date}
                  onChange={(e) => setTalk({ ...talk, date: e.target.value })}
                  className="bg-white border-stone-200 text-sm"
                  data-testid="talk-date-input"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#8C9A7C] block mb-1.5">Time</label>
                <Input
                  type="time"
                  value={talk.time}
                  onChange={(e) => setTalk({ ...talk, time: e.target.value })}
                  className="bg-white border-stone-200 text-sm"
                  data-testid="talk-time-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#8C9A7C] block mb-1.5">Where</label>
              <div className="grid grid-cols-3 gap-2">
                {["Living Room", "Terrace", "Courtyard"].map((loc) => (
                  <button
                    key={loc}
                    onClick={() => setTalk({ ...talk, location: loc })}
                    className={`text-xs rounded-lg py-2 px-1 border transition-colors ${
                      talk.location === loc
                        ? "bg-[#C85A38] text-white border-[#C85A38]"
                        : "bg-white text-[#1A1A1A]/70 border-stone-200"
                    }`}
                    data-testid={`talk-location-${loc.toLowerCase().replace(" ", "-")}`}
                  >
                    {loc}
                  </button>
                ))}
              </div>
            </div>
            <Button
              onClick={submitTalk}
              disabled={submittingTalk}
              className="w-full bg-[#C85A38] hover:bg-[#B85540] text-white rounded-lg h-11"
              data-testid="talk-submit-btn"
            >
              {submittingTalk ? "Scheduling…" : "Put it on the calendar"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* First week tips sheet */}
      <Sheet open={firstWeekOpen} onOpenChange={setFirstWeekOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl bg-[#FBF7F1] max-h-[88vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="font-serif text-2xl text-[#1A1A1A]">Your first week at Savi</SheetTitle>
            <SheetDescription className="text-sm text-[#1A1A1A]/55">A few things that help newcomers settle in.</SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-2.5 pb-4">
            {FIRST_WEEK_TIPS.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-stone-200">
                <IconChip icon={tip.icon} tone="sage" />
                <div>
                  <p className="font-medium text-[#1A1A1A] text-sm">{tip.title}</p>
                  <p className="text-xs text-[#1A1A1A]/55 mt-0.5 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
